import { ensureChatTabsInitialized } from "./app-chat-tabs.ts";
import { flushChatQueueForEvent } from "./app-chat.ts";
import type { EventLogEntry } from "./app-events.ts";
import { stopChatPolling, syncChatPolling } from "./app-polling.ts";
import {
  applySessionArtifactRecordedEvent,
  clearRunArtifactPreviews,
  hydrateRunArtifactPreviews,
} from "./app-run-artifacts.ts";
import {
  applySettings,
  loadCron,
  refreshActiveTab,
  setLastActiveSessionKey,
} from "./app-settings.ts";
import { handleAgentEvent, resetToolStream, type AgentEventPayload } from "./app-tool-stream.ts";
import type { OpenClawApp } from "./app.ts";
import { shouldDeferChatHistoryReload } from "./chat/chat-history-defer.ts";
import { refreshChatLastRunFiles } from "./chat/chat-last-run-files.ts";
import { stopChatWaitTicker, clearChatWaitState } from "./chat/chat-wait-ticker.ts";
import { extractText } from "./chat/message-extract.ts";
import { lookupSubagentCard, patchSubagentCardEverywhere } from "./chat/subagents-by-session.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadAssistantIdentity } from "./controllers/assistant-identity.ts";
import { loadChatContextBreakdown } from "./controllers/chat-context-breakdown.ts";
import {
  loadChatPalaceContinuity,
  refreshChatPalaceContinuityAfterRun,
  cacheChatPalaceContinuity,
} from "./controllers/chat-palace-continuity.ts";
import { loadChatHistory, refreshChatMessagesCacheForSession } from "./controllers/chat.ts";
import {
  handleChatEvent,
  handleChatRunEvent,
  type ChatEventPayload,
  type ChatRunEventPayload,
} from "./controllers/chat.ts";
import { handleControlUiBuildEvent } from "./controllers/developer.ts";
import { loadDevices } from "./controllers/devices.ts";
import type { ExecApprovalRequest } from "./controllers/exec-approval.ts";
import {
  addExecApproval,
  parseExecApprovalRequested,
  parseExecApprovalResolved,
  removeExecApproval,
} from "./controllers/exec-approval.ts";
import {
  addFleetQuestion,
  parseFleetQuestionRequested,
  parseFleetQuestionResolved,
  removeFleetQuestion,
} from "./controllers/fleet-question.ts";
import { parseFleetTodosUpdated } from "./controllers/fleet-todos.ts";
import {
  loadFleetSessionActivity,
  type FleetSessionActivityView,
} from "./controllers/fleet-work-plan.ts";
import { loadMultiagentTemplates } from "./controllers/multi-agent-templates.ts";
import { loadNodes } from "./controllers/nodes.ts";
import {
  applySubagentStreamDelta,
  upsertSubagentCardFromChatRun,
} from "./controllers/subagents.ts";
import type { GatewayEventFrame, GatewayHelloOk } from "./gateway.ts";
import { GatewayBrowserClient } from "./gateway.ts";
import type { Tab } from "./navigation.ts";
import { isFleetConsoleTab } from "./navigation.ts";
import {
  handleChatTurnEvent,
  handleOperatorTimelineEvent,
  handleSessionHealthEvent,
} from "./operator-trust/handlers.ts";
import type { UiSettings } from "./storage.ts";
import type { AgentsListResult, PresenceEntry, HealthSnapshot, StatusSummary } from "./types.ts";
import type { ChatSessionContextMeta } from "./views/chat-session-context.ts";

type GatewayHost = {
  settings: UiSettings;
  password: string;
  client: GatewayBrowserClient | null;
  connected: boolean;
  hello: GatewayHelloOk | null;
  lastError: string | null;
  onboarding?: boolean;
  /**
   * JAISIU-XXXX — wall-clock ms of the last `onRecoverableGap` /
   * reconnect-induced history refetch. Used by the `chat.final` handler
   * to skip a redundant refetch if the gap recovery already pulled a
   * fresh snapshot within the last 2s. Prevents the two-source race
   * where the gap recovery's RPC and the chat.final's RPC land out of
   * order and the second paints stale state on top of the first.
   */
  lastGapRecoveredAt?: number;
  eventLogBuffer: EventLogEntry[];
  /**
   * Per-session ring buffers of gateway events. Each session key maps to a
   * most-recent-first array, capped at PER_SESSION_EVENT_RING entries. The
   * inspector Background tab reads from this ring instead of scanning the
   * global `eventLogBuffer` (which evicts oldest-first and can drop a
   * session's history before the user switches back to it).
   */
  eventLogBySession: Record<string, EventLogEntry[]>;
  eventLog: EventLogEntry[];
  tab: Tab;
  presenceEntries: PresenceEntry[];
  presenceError: string | null;
  presenceStatus: StatusSummary | null;
  agentsLoading: boolean;
  agentsList: AgentsListResult | null;
  agentsError: string | null;
  debugHealth: HealthSnapshot | null;
  assistantName: string;
  assistantAvatar: string | null;
  assistantAgentId: string | null;
  sessionKey: string;
  chatRunId: string | null;
  chatSending: boolean;
  refreshSessionsAfterChat: Set<string>;
  execApprovalQueue: ExecApprovalRequest[];
  execApprovalError: string | null;
  fleetQuestionQueue: import("./controllers/fleet-question.ts").FleetQuestionRequest[];
  fleetQuestionDraft: Record<string, string[]>;
  fleetQuestionFreeText: Record<string, string>;
  fleetTodosBySession: Record<string, import("./controllers/fleet-todos.ts").FleetTodoItem[]>;
  chatSubagents?: import("./ui-types.ts").ChatSubagentCard[];
  bumpSessionTabUnread?: (sessionKey: string) => void;
  getChatOpenTabs?: () => string[];
};

type SessionDefaultsSnapshot = {
  defaultAgentId?: string;
  mainKey?: string;
  mainSessionKey?: string;
  scope?: string;
};

/** Capacity of the global event buffer (most recent first). */
const EVENT_LOG_BUFFER = 250;
/**
 * Capacity of the per-session event ring. Picked to comfortably cover an
 * inspector Background tab render (40) plus headroom for child session keys.
 */
const PER_SESSION_EVENT_RING = 50;
/**
 * Heuristic list of session-key JSON keys to scan when classifying a gateway
 * event payload into a session. Falls back to "global" when no key matches —
 * those events still appear in the per-session ring so the Background tab
 * can show them when the user is on the matching session.
 */
const SESSION_KEY_PAYLOAD_FIELDS = [
  "sessionKey",
  "childSessionKey",
  "parentSessionKey",
  "session",
  "sessionId",
  "session_id",
  "key",
] as const;

function extractSessionKeysFromPayload(payload: unknown): string[] {
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const out = new Set<string>();
  const obj = payload as Record<string, unknown>;
  for (const field of SESSION_KEY_PAYLOAD_FIELDS) {
    const value = obj[field];
    if (typeof value === "string" && value.trim()) {
      out.add(value.trim());
    }
  }
  const sessions = obj.sessions;
  if (Array.isArray(sessions)) {
    for (const entry of sessions) {
      if (typeof entry === "string" && entry.trim()) {
        out.add(entry.trim());
      } else if (entry && typeof entry === "object") {
        const maybeKey = (entry as Record<string, unknown>).sessionKey;
        if (typeof maybeKey === "string" && maybeKey.trim()) {
          out.add(maybeKey.trim());
        }
      }
    }
  }
  return [...out];
}

/**
 * How many session keys keep a resident ring.
 *
 * Depth was always capped (`PER_SESSION_EVENT_RING`), but the KEY COUNT was
 * not: `extractSessionKeysFromPayload` also harvests keys out of
 * `payload.sessions` arrays, so one fleet-wide event could mint dozens of
 * keys at once, and nothing ever removed them — only `connectGateway` cleared
 * the whole record. Each key holds up to 50 entries.
 */
const PER_SESSION_EVENT_RING_MAX_KEYS = 64;

/**
 * Insertion-ordered key list for eviction. A plain `Object.keys(ring)` scan on
 * every event would be O(keys) per event, and `getGatewayEventsBySession`
 * already copies the whole record on each render.
 */
const perSessionEventKeyOrder: string[] = [];

/** Drop all tracked ring keys (called when the ring record is replaced). */
export function resetPerSessionEventRingKeys(): void {
  perSessionEventKeyOrder.length = 0;
  ringBySessionRef = {};
}

function rememberEventRingKey(key: string): void {
  const existing = perSessionEventKeyOrder.indexOf(key);
  if (existing >= 0) {
    perSessionEventKeyOrder.splice(existing, 1);
  }
  perSessionEventKeyOrder.push(key);
  while (perSessionEventKeyOrder.length > PER_SESSION_EVENT_RING_MAX_KEYS) {
    const oldest = perSessionEventKeyOrder.shift();
    if (typeof oldest !== "string") break;
    delete ringBySessionRef[oldest];
  }
}

/**
 * Shared handle so the key-order bookkeeping and the ring share one object.
 * Assigned at connect time; every writer goes through `pushPerSessionRing`.
 */
let ringBySessionRef: Record<string, EventLogEntry[]> = {};

export function pushPerSessionRing(
  ring: Record<string, EventLogEntry[]>,
  entry: EventLogEntry,
  sessionKeys: string[],
): void {
  ringBySessionRef = ring;
  for (const key of sessionKeys) {
    const existing = ring[key];
    rememberEventRingKey(key);
    if (!existing) {
      ring[key] = [entry];
      continue;
    }
    const next = [entry, ...existing];
    if (next.length > PER_SESSION_EVENT_RING) {
      ring[key] = next.slice(0, PER_SESSION_EVENT_RING);
    } else {
      ring[key] = next;
    }
  }
}

function normalizeSessionKeyForDefaults(
  value: string | undefined,
  defaults: SessionDefaultsSnapshot,
): string {
  const raw = (value ?? "").trim();
  const mainSessionKey = defaults.mainSessionKey?.trim();
  if (!mainSessionKey) {
    return raw;
  }
  if (!raw) {
    return mainSessionKey;
  }
  const mainKey = defaults.mainKey?.trim() || "main";
  const defaultAgentId = defaults.defaultAgentId?.trim();
  const isAlias =
    raw === "main" ||
    raw === mainKey ||
    (defaultAgentId &&
      (raw === `agent:${defaultAgentId}:main` || raw === `agent:${defaultAgentId}:${mainKey}`));
  return isAlias ? mainSessionKey : raw;
}

function applySessionDefaults(host: GatewayHost, defaults?: SessionDefaultsSnapshot) {
  if (!defaults?.mainSessionKey) {
    return;
  }
  const resolvedSessionKey = normalizeSessionKeyForDefaults(host.sessionKey, defaults);
  const resolvedSettingsSessionKey = normalizeSessionKeyForDefaults(
    host.settings.sessionKey,
    defaults,
  );
  const resolvedLastActiveSessionKey = normalizeSessionKeyForDefaults(
    host.settings.lastActiveSessionKey,
    defaults,
  );
  const nextSessionKey = resolvedSessionKey || resolvedSettingsSessionKey || host.sessionKey;
  const nextSettings = {
    ...host.settings,
    sessionKey: resolvedSettingsSessionKey || nextSessionKey,
    lastActiveSessionKey: resolvedLastActiveSessionKey || nextSessionKey,
  };
  const shouldUpdateSettings =
    nextSettings.sessionKey !== host.settings.sessionKey ||
    nextSettings.lastActiveSessionKey !== host.settings.lastActiveSessionKey;
  if (nextSessionKey !== host.sessionKey) {
    host.sessionKey = nextSessionKey;
  }
  if (shouldUpdateSettings) {
    applySettings(host as unknown as Parameters<typeof applySettings>[0], nextSettings);
  }
}

export function connectGateway(host: GatewayHost) {
  host.lastError = null;
  host.hello = null;
  host.connected = false;
  // Fresh connection means the per-session event rings start over. Reset the
  // key-order bookkeeping alongside the record so the eviction bound in
  // `pushPerSessionRing` is applied to the new object, not the old one.
  host.eventLogBySession = {};
  resetPerSessionEventRingKeys();
  host.execApprovalQueue = [];
  host.execApprovalError = null;
  host.fleetQuestionQueue = [];
  host.fleetQuestionDraft = {};
  host.fleetQuestionFreeText = {};

  host.client?.stop();
  // Per-session, per-host tunnel refactor — bind the controller BEFORE
  // the new client is created. `bind()` is idempotent; it stashes the
  // tunnel-event and heartbeat callbacks on the host, which we then
  // forward to the GatewayBrowserClient options below. The controller
  // owns the heartbeat tick (5s) and the dead→cancel side effects;
  // the client only emits transport events.
  const tunnelCtl = (host as unknown as {
    tunnelHealthController?: import("./controllers/tunnel-health.ts").TunnelHealthController;
  }).tunnelHealthController;
  if (tunnelCtl) {
    tunnelCtl.bind(
      host as unknown as Parameters<typeof tunnelCtl.bind>[0] & {
        onTunnelDead?: (key: { sessionId: string; hostId: string }) => void;
        onTunnelRecordsChanged?: () => void;
      },
    );
    (host as unknown as { onTunnelDead?: (key: { sessionId: string; hostId: string }) => void }).onTunnelDead = (key) => {
      // Per acceptance criterion #4: when a tunnel dies, all affected
      // work units are paused/cancelled and their subagents are no
      // longer marked RUNNING. We do NOT set `host.lastError` from
      // this path — that is the audit-flagged composer pollution.
      const cancelHost = host as unknown as Parameters<typeof import("./controllers/tunnel-health.ts").cancelWorkUnitsForDeadTunnel>[0];
      void import("./controllers/tunnel-health.ts").then((m) =>
        m.cancelWorkUnitsForDeadTunnel(cancelHost, key),
      );
    };
  }
  const tunnelCallbacks = (host as unknown as {
    tunnelCallbacks?: {
      onEvent: (evt: { state: "connecting" | "healthy" | "degraded" | "dead"; code?: number; reason?: string }) => void;
      onHeartbeat: () => void;
    };
  }).tunnelCallbacks;
  host.client = new GatewayBrowserClient({
    url: host.settings.gatewayUrl,
    token: host.settings.token.trim() ? host.settings.token : undefined,
    password: host.password.trim() ? host.password : undefined,
    clientName: "openclaw-control-ui",
    mode: "webchat",
    onHello: (hello) => {
      host.connected = true;
      host.lastError = null;
      host.hello = hello;
      // JAISIU-4089 — websocket just (re)connected. Clear the failure
      // buffer and re-evaluate the verdict so the topbar pill flips
      // back to "online" without waiting a full 5s poll.
      (
        host as unknown as { noteSessionHealthReconnect?: () => void }
      ).noteSessionHealthReconnect?.();
      // JAISIU-XXXX — mark the moment we reconnected so a chat.final
      // event landing in the same tick can skip its redundant refetch
      // (the gap-recovery path at line 313 already pulled a fresh
      // snapshot). Without this guard, two loadChatHistory RPCs race and
      // whichever finishes second paints a stale snapshot.
      host.lastGapRecoveredAt = Date.now();
      applySnapshot(host, hello);
      ensureChatTabsInitialized(host as unknown as Parameters<typeof ensureChatTabsInitialized>[0]);
      // Reset orphaned chat run state from before disconnect.
      // Any in-flight run's final event was lost during the disconnect window.
      // Use the helper so the live-wait ticker interval is torn down
      // synchronously before re-nulling state, otherwise a stranded
      // setInterval keeps painting a stuck "waiting for answer" bubble
      // with stale elapsed time until Lit's next render detects the
      // property changes.
      clearChatWaitState(host as unknown as Parameters<typeof clearChatWaitState>[0]);
      resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
      clearRunArtifactPreviews(host as unknown as OpenClawApp);
      void loadAssistantIdentity(host as unknown as OpenClawApp);
      void loadAgents(host as unknown as OpenClawApp);
      void loadMultiagentTemplates(host.client, host.connected);
      void loadNodes(host as unknown as OpenClawApp, { quiet: true });
      void loadDevices(host as unknown as OpenClawApp, { quiet: true });
      if (isFleetConsoleTab(host.tab)) {
        void loadChatHistory(host as unknown as OpenClawApp, { force: true, fullHistory: true });
      } else if (
        Array.isArray((host as unknown as OpenClawApp).pendingUserMessageIds) &&
        (host as unknown as OpenClawApp).pendingUserMessageIds!.length > 0
      ) {
        // Reconnect with optimistic user rows still pending — pin JSONL
        // ground truth without waiting for the next chat.final.
        void loadChatHistory(host as unknown as OpenClawApp, {
          quiet: true,
          force: true,
          fullHistory: true,
        });
      }
      syncChatPolling(host as unknown as Parameters<typeof syncChatPolling>[0]);
      // Fleet Control staleness verification — clear the disconnect
      // stamp on reconnect. The onConnect block above triggers a
      // loadChatHistory which refreshes lastSyncedAt. Leave
      // lastEventAt alone; a stale value here only matters until the
      // next chat event lands.
      (host as unknown as OpenClawApp).chatSync = {
        lastSyncedAt:
          (
            host as {
              chatSync?: {
                lastSyncedAt: number | null;
                lastEventAt: number | null;
                lastWsCloseAt: number | null;
              };
            }
          ).chatSync?.lastSyncedAt ?? null,
        lastEventAt:
          (
            host as {
              chatSync?: {
                lastSyncedAt: number | null;
                lastEventAt: number | null;
                lastWsCloseAt: number | null;
              };
            }
          ).chatSync?.lastEventAt ?? null,
        lastWsCloseAt: null,
      };
      if (host.sessionKey.trim()) {
        void loadChatPalaceContinuity(host as unknown as OpenClawApp, { quiet: true });
      }
      void refreshActiveTab(host as unknown as Parameters<typeof refreshActiveTab>[0]);
      void (host as unknown as OpenClawApp).refreshFlociAgents?.();
      void (host as unknown as OpenClawApp).refreshSubagentRegistry?.();
      void (host as unknown as OpenClawApp).refreshFleetProjects?.();
      void (host as unknown as OpenClawApp).refreshFleetInteraction?.();
      void hydrateRunArtifactPreviews(host as unknown as OpenClawApp);
    },
    onClose: ({ code, reason }) => {
      host.connected = false;
      stopChatPolling(host as unknown as Parameters<typeof stopChatPolling>[0]);
      // Code 1012 = Service Restart (expected during config saves, don't show as error)
      if (code !== 1012) {
        host.lastError = `disconnected (${code}): ${reason || "no reason"}`;
      }
      // JAISIU-4089 — websocket dropped. Note a failure so the
      // pill flips to "offline" immediately. The monitor poll will
      // keep the verdict in sync until the next hello.
      (
        host as unknown as { noteSessionHealthFailure?: (r: string) => void }
      ).noteSessionHealthFailure?.(`WS_CLOSE_${code}`);
      // Fleet Control staleness verification — stamp WS close time so
      // the chat header pill flips to "Disconnected" within 1s.
      // (`lastSyncedAt`/`lastEventAt` are preserved so reconnect can
      // reason about ages without losing history.)
      const existingSync = (
        host as {
          chatSync?: {
            lastSyncedAt: number | null;
            lastEventAt: number | null;
            lastWsCloseAt: number | null;
          };
        }
      ).chatSync;
      (host as unknown as OpenClawApp).chatSync = {
        lastSyncedAt: existingSync?.lastSyncedAt ?? null,
        lastEventAt: existingSync?.lastEventAt ?? null,
        lastWsCloseAt: Date.now(),
      };
    },
    onEvent: (evt) => handleGatewayEvent(host, evt),
    // Per-session/per-host tunnel refactor — forward the controller's
    // callbacks to the client. The controller owns the state machine;
    // the client just reports transport transitions and heartbeat
    // samples. Neither path writes to `host.lastError`.
    onTunnelEvent: tunnelCallbacks?.onEvent,
    onTunnelHeartbeat: tunnelCallbacks?.onHeartbeat,
    onGap: ({ expected, received, recoverable }) => {
      if (recoverable) {
        // Recoverable gap — lastSeq was null (we just reconnected) and the
        // first event after handshake would have triggered a gap warning.
        // Quietly refetch history; don't surface a "refresh the page" toast.
        // (Recoverable gaps are also surfaced via onRecoverableGap below.)
        return;
      }
      host.lastError = `event gap detected (expected seq ${expected}, got ${received}); refresh recommended`;
    },
    onRecoverableGap: () => {
      // Connection just re-established; refetch chat history so the UI
      // catches up to the server's broadcast counter without a manual
      // refresh. This is the cheap-fix path; the upstream onGap handler
      // keeps the toast for the unrecoverable case.
      //
      // JAISIU-XXXX — force + quiet. Without `force: true`, a quiet poll
      // already in flight will gate this refetch out, and the user sees
      // stale transcript until the next manual action. Without `quiet:
      // true`, we'd flash the chat-loading overlay on every reconnect.
      void loadChatHistory(host as unknown as OpenClawApp, {
        force: true,
        quiet: true,
        fullHistory: true,
      });
      // JAISIU-XXXX — also refresh Palace continuity on reconnect so the
      // dropdown doesn't show stale points / handoff state captured
      // before the gap.
      if (host.sessionKey.trim()) {
        void loadChatPalaceContinuity(host as unknown as OpenClawApp, { quiet: true });
      }
    },
  });
  host.client.start();
}

/**
 * JAISIU-quantum-sync-storm / subagent-drag — swap-and-restore helper.
 *
 * When the operator is viewing session A and a `chat.final` lands for
 * session B (often a subagent that just finished), the palace-continuity
 * cache must still be filled for session B. `loadChatPalaceContinuity`
 * reads `host.sessionKey`, so we:
 *
 *   1. snapshot the operator's current sessionKey + continuity state
 *   2. overwrite them with the event's session
 *   3. invoke the load
 *   4. cache the snapshot
 *   5. **always** restore in `.finally` — even if the load rejects,
 *      the browser tab throttles the load, or the import-callback
 *      chain throws synchronously
 *
 * Pre-fix step 5 was inside `.then(...)`, so any rejection left the
 * operator with `sessionKey = eventSessionKey` — surfacing as
 * "the system dragged me into a subagent session after the agent
 * finished". The `.finally` block makes that path impossible.
 */
export function swapAndRestorePalaceContinuity(host: OpenClawApp, eventSessionKey: string): void {
  const hostAny = host as unknown as {
    sessionKey: string;
    chatPalaceContinuity: unknown;
    chatPalaceContinuityError: unknown;
  };
  const restoreKey = hostAny.sessionKey;
  const restoreSnapshot = hostAny.chatPalaceContinuity;
  const restoreError = hostAny.chatPalaceContinuityError;
  hostAny.sessionKey = eventSessionKey;
  hostAny.chatPalaceContinuity = null;
  hostAny.chatPalaceContinuityError = null;
  void loadChatPalaceContinuity(host as unknown as OpenClawApp, {
    quiet: true,
  })
    .then(() => {
      const snap = hostAny.chatPalaceContinuity as
        | import("./controllers/chat-palace-continuity.ts").PalaceContinuitySnapshot
        | null;
      if (snap) {
        void import("./controllers/chat-palace-continuity.ts").then(
          ({ cacheChatPalaceContinuity }) => {
            cacheChatPalaceContinuity(
              host as unknown as Parameters<typeof cacheChatPalaceContinuity>[0],
              eventSessionKey,
              snap,
            );
          },
        );
      }
    })
    .catch((err) => {
      // Swallow but log — the fetch was opportunistic anyway. The most
      // important thing is that the `.finally` below runs and the
      // operator's view is restored.
      host.lastError = String(err);
    })
    .finally(() => {
      // Restore the operator's view. Runs on resolve, reject, AND any
      // synchronous throw from the import-callback chain — so the
      // operator can never get stranded on the event's session.
      hostAny.sessionKey = restoreKey;
      hostAny.chatPalaceContinuity = restoreSnapshot;
      hostAny.chatPalaceContinuityError = restoreError;
    });
}

export function handleGatewayEvent(host: GatewayHost, evt: GatewayEventFrame) {
  try {
    handleGatewayEventUnsafe(host, evt);
  } catch (err) {
    console.error("[gateway] handleGatewayEvent error:", evt.event, err);
  }
}

function handleGatewayEventUnsafe(host: GatewayHost, evt: GatewayEventFrame) {
  const entry: EventLogEntry = {
    ts: Date.now(),
    event: evt.event,
    payload: evt.payload,
  };
  host.eventLogBuffer = [entry, ...host.eventLogBuffer].slice(0, EVENT_LOG_BUFFER);
  if ("gatewayEventTail" in host) {
    (host as { gatewayEventTail: EventLogEntry[] }).gatewayEventTail = host.eventLogBuffer;
  }
  if (host.tab === "debug") {
    host.eventLog = host.eventLogBuffer;
  }

  // Per-session ring: classify the event by session-key fields so the
  // inspector Background tab can show this session's history even after the
  // global buffer evicts older entries. Falls back to "__global__" when the
  // payload doesn't reference a session — those still appear on the active
  // session's tab as "unclassified".
  const sessionKeys = extractSessionKeysFromPayload(evt.payload);
  const ring = host.eventLogBySession ?? (host.eventLogBySession = {});
  // Keep the module-level eviction bookkeeping pointed at the live record so
  // `rememberEventRingKey` deletes from the same object it is bounding.
  ringBySessionRef = ring;
  if (sessionKeys.length === 0) {
    pushPerSessionRing(ring, entry, ["__global__"]);
  } else {
    pushPerSessionRing(ring, entry, sessionKeys);
  }

  if (evt.event === "controlUi.build") {
    handleControlUiBuildEvent(
      host as unknown as import("./app.ts").OpenClawApp,
      evt.payload as import("./controllers/developer.ts").ControlUiBuildEventPayload,
    );
    return;
  }

  if (evt.event === "agent") {
    if (host.onboarding) {
      return;
    }
    handleAgentEvent(
      host as unknown as Parameters<typeof handleAgentEvent>[0],
      evt.payload as AgentEventPayload | undefined,
    );
    return;
  }

  if (evt.event === "session.health") {
    handleSessionHealthEvent(
      host as unknown as Parameters<typeof handleSessionHealthEvent>[0],
      (evt.payload ?? {}) as Parameters<typeof handleSessionHealthEvent>[1],
    );
    return;
  }

  if (evt.event === "chat.turn") {
    handleChatTurnEvent(
      host as unknown as Parameters<typeof handleChatTurnEvent>[0],
      (evt.payload ?? {}) as Parameters<typeof handleChatTurnEvent>[1],
    );
    return;
  }

  if (evt.event === "operator.timeline") {
    handleOperatorTimelineEvent(
      host as unknown as Parameters<typeof handleOperatorTimelineEvent>[0],
      (evt.payload ?? {}) as Parameters<typeof handleOperatorTimelineEvent>[1],
    );
    return;
  }

  if (evt.event === "fleet.plan") {
    const payload = evt.payload as
      | {
          planId?: string;
          sessionKey?: string;
          spawned?: Array<{
            unitId?: string;
            unitTitle?: string;
            childSessionKey?: string;
            runId?: string;
          }>;
        }
      | undefined;
    const sessionKey = payload?.sessionKey?.trim();
    if (sessionKey && sessionKey === host.sessionKey.trim()) {
      for (const row of payload?.spawned ?? []) {
        const childSessionKey = row.childSessionKey?.trim();
        const runId = row.runId?.trim();
        if (!childSessionKey || !runId) {
          continue;
        }
        host.chatSubagents = upsertSubagentCardFromChatRun(host.chatSubagents ?? [], {
          runId,
          childSessionKey,
          label: row.unitTitle?.trim() || row.unitId?.trim() || "unit",
        });
      }
      void refreshFleetSessionActivity(host, sessionKey);
    }
    return;
  }

  if (evt.event === "chat.run") {
    const payload = evt.payload as ChatRunEventPayload | undefined;
    handleChatRunEvent(host as unknown as Parameters<typeof handleChatRunEvent>[0], payload);
    if (
      payload?.origin === "subagent" &&
      payload.childSessionKey?.trim() &&
      payload.runId?.trim() &&
      payload.sessionKey?.trim() === host.sessionKey.trim()
    ) {
      host.chatSubagents = upsertSubagentCardFromChatRun(host.chatSubagents ?? [], {
        runId: payload.runId,
        childSessionKey: payload.childSessionKey,
        label: payload.label,
      });
    }
    return;
  }

  if (evt.event === "chat") {
    const payload = evt.payload as ChatEventPayload | undefined;

    const childKey = payload?.sessionKey?.trim();
    if (childKey && childKey !== host.sessionKey.trim()) {
      const onActiveList = host.chatSubagents?.some((c) => c.childSessionKey === childKey);
      const stashHost = host as GatewayHost & {
        sessionsResult?: import("./types.ts").SessionsListResult | null;
        chatTabMeta?: Record<string, ChatSessionContextMeta>;
      };
      const inStash =
        !onActiveList &&
        lookupSubagentCard(
          host as object,
          childKey,
          stashHost.sessionsResult ?? null,
          stashHost.chatTabMeta,
        ) !== null;
      if (onActiveList || inStash) {
        if (payload?.state === "delta") {
          host.bumpSessionTabUnread?.(childKey);
          const next = extractText(payload.message);
          if (typeof next === "string") {
            // JAISIU-XXXX — append vs overwrite. The wire protocol ships
            // the FULL accumulated stream in each delta payload, not the
            // chunk delta. So strictly overwrite is correct for normal
            // WS traffic. BUT: if the card already has streamText from a
            // previous connection session (reconnect / replay / registry
            // restore), overwrite would drop the prefix the user already
            // saw rendered. Treat "next starts with current streamText"
            // as the canonical case (no change) and otherwise preserve
            // the existing prefix when next contains it (append mode).
            const applyDelta = (c: import("./ui-types.ts").ChatSubagentCard) => {
              if (c.childSessionKey !== childKey) return c;
              let merged = next;
              if (c.streamText && next.startsWith(c.streamText)) {
                merged = next;
              } else if (
                c.streamText &&
                c.streamText.length > 0 &&
                next.length > c.streamText.length &&
                c.streamText.endsWith(next.slice(0, Math.max(0, next.length - c.streamText.length)))
              ) {
                merged = next;
              }
              return applySubagentStreamDelta(c, merged);
            };
            if (onActiveList) {
              host.chatSubagents = (host.chatSubagents ?? []).map((c) =>
                c.childSessionKey === childKey ? applyDelta(c) : c,
              );
            } else {
              patchSubagentCardEverywhere(
                host as object,
                { childSessionKey: childKey },
                applyDelta,
                host.chatSubagents,
                (cards) => {
                  host.chatSubagents = cards;
                },
              );
            }
          }
        }
        if (payload?.state === "final" || payload?.state === "error") {
          const applyFinal = (c: import("./ui-types.ts").ChatSubagentCard) => ({
            ...c,
            status: payload?.state === "error" ? ("error" as const) : ("done" as const),
            error:
              payload?.state === "error"
                ? (payload.errorMessage ?? c.error ?? "chat error")
                : c.error,
          });
          if (onActiveList) {
            host.chatSubagents = (host.chatSubagents ?? []).map((c) =>
              c.childSessionKey === childKey ? applyFinal(c) : c,
            );
          } else {
            patchSubagentCardEverywhere(
              host as object,
              { childSessionKey: childKey },
              applyFinal,
              host.chatSubagents,
              (cards) => {
                host.chatSubagents = cards;
              },
            );
          }
        }
        return;
      }
    }
    if (payload?.sessionKey) {
      setLastActiveSessionKey(
        host as unknown as Parameters<typeof setLastActiveSessionKey>[0],
        payload.sessionKey,
      );
    }
    const eventKey = payload?.sessionKey?.trim();
    if (
      payload?.state === "delta" &&
      eventKey &&
      eventKey !== host.sessionKey.trim() &&
      host.getChatOpenTabs?.().includes(eventKey)
    ) {
      host.bumpSessionTabUnread?.(eventKey);
    }
    const app = host as unknown as OpenClawApp;
    app.wireChatControllerState(app);
    const state = handleChatEvent(app, payload);
    const eventSessionKey = payload?.sessionKey?.trim() ?? "";
    if (
      payload?.state === "final" &&
      eventSessionKey &&
      eventSessionKey !== host.sessionKey.trim() &&
      host.getChatOpenTabs?.().includes(eventSessionKey)
    ) {
      void refreshChatMessagesCacheForSession(app, eventSessionKey);
    }
    if (state === "final" || state === "error" || state === "aborted") {
      if (state === "final" && eventSessionKey && eventSessionKey === host.sessionKey.trim()) {
        void refreshChatLastRunFiles(app);
        void loadChatContextBreakdown(
          app as unknown as Parameters<typeof loadChatContextBreakdown>[0],
        );
        // A fan-in synthesis is requested via `chat.inject`, so the parent's
        // reply to it arrives as an ordinary final. Flip the fan-in bar from
        // "Synthesizing…" to "Synthesis complete" here rather than waiting
        // for a poll — this is the signal that the operator's verdict landed.
        (app as unknown as OpenClawApp).markFanInSynthesized?.(eventSessionKey);
      }
      (host as unknown as { chatLiveRunHint: string | null }).chatLiveRunHint = null;
      (host as unknown as { chatActiveFileToolPath: string | null }).chatActiveFileToolPath = null;
      clearRunArtifactPreviews(host as unknown as OpenClawApp, host.sessionKey);
      (host as unknown as OpenClawApp).chatMainRun = null;
      // Matching-run terminal events already stop the ticker in handleChatEvent;
      // call again here so mismatched-run finals (which return "final" without
      // clearing chatRunId) still tear down any orphaned measuring interval
      // only when the host is no longer busy.
      if (!host.chatSending && !host.chatRunId) {
        stopChatWaitTicker(host as unknown as Parameters<typeof stopChatWaitTicker>[0]);
      }
      resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
      void flushChatQueueForEvent(host as unknown as Parameters<typeof flushChatQueueForEvent>[0]);
      const runId = payload?.runId;
      if (runId && host.refreshSessionsAfterChat.has(runId)) {
        host.refreshSessionsAfterChat.delete(runId);
        if (state === "final") {
          // Per spec: refresh the FULL chat-history catalog (no
          // `activeMinutes` filter). The 120-min filter would
          // overwrite `state.sessionsResult` and make the chat-history
          // sidebar lose every older row.
          void (host as unknown as OpenClawApp).refreshChatHistorySessions?.();
        }
      }
    }
    if (state === "final") {
      // JAISIU-XXXX — skip the refetch if a gap recovery just fired in
      // the last 2s. The gap-recovery path already pulled a fresh
      // snapshot; refetching again races and the second response can
      // paint a stale snapshot on top of the first.
      const gapRecent =
        typeof host.lastGapRecoveredAt === "number" && Date.now() - host.lastGapRecoveredAt < 2_000;
      const deferReload = shouldDeferChatHistoryReload(
        host as unknown as Parameters<typeof shouldDeferChatHistoryReload>[0],
        { runId: payload?.runId },
      );
      if (!gapRecent && !deferReload) {
        void loadChatHistory(host as unknown as OpenClawApp, { quiet: true, force: true });
      }
      if (eventSessionKey) {
        // JAISIU-XXXX — refresh continuity for the EVENT's session
        // regardless of which session the operator is currently viewing.
        // The result is stashed in a per-session cache; if the operator
        // is on this session too, the result also becomes the active
        // snapshot. If they're elsewhere, the cache write keeps the
        // data fresh for when they switch back — no more "snap to empty"
        // until the next chat.final on that session.
        const cacheHost = host as unknown as OpenClawApp;
        if (eventSessionKey === host.sessionKey.trim()) {
          void refreshChatPalaceContinuityAfterRun(cacheHost).then(() => {
            // refreshChatPalaceContinuityAfterRun mutates
            // state.chatPalaceContinuity in place; mirror into the cache
            // so the session-switch path can hydrate from it.
            if (cacheHost.chatPalaceContinuity) {
              // Defer import to avoid circular dep at module-load time.
              void import("./controllers/chat-palace-continuity.ts").then(
                ({ cacheChatPalaceContinuity }) => {
                  cacheChatPalaceContinuity(
                    cacheHost,
                    eventSessionKey,
                    cacheHost.chatPalaceContinuity!,
                  );
                },
              );
            }
          });
        } else {
          // Operator is on a different session — fetch continuity for the
          // event session silently and stash it. The swap-and-restore
          // logic lives in `swapAndRestorePalaceContinuity` (defined
          // just below `connectGateway`); we extracted it so the restore
          // path could be unit-tested directly.
          //
          // JAISIU-quantum-sync-storm / subagent-drag — the restore runs
          // in `.finally` inside the helper, so the operator can never
          // get stranded on the event's session even if the load rejects.
          swapAndRestorePalaceContinuity(host as unknown as OpenClawApp, eventSessionKey);
        }
      }
      if (isFleetConsoleTab(host.tab)) {
        // Same: refresh the full chat-history catalog on run final.
        void (host as unknown as OpenClawApp).refreshChatHistorySessions?.();
        void (
          host as unknown as { refreshChatWorkspace?: () => Promise<void> }
        ).refreshChatWorkspace?.();
      }
    }
    return;
  }

  if (evt.event === "presence") {
    const payload = evt.payload as { presence?: PresenceEntry[] } | undefined;
    if (payload?.presence && Array.isArray(payload.presence)) {
      host.presenceEntries = payload.presence;
      host.presenceError = null;
      host.presenceStatus = null;
    }
    return;
  }

  if (evt.event === "cron" && host.tab === "cron") {
    void loadCron(host as unknown as Parameters<typeof loadCron>[0]);
  }

  if (evt.event === "device.pair.requested" || evt.event === "device.pair.resolved") {
    void loadDevices(host as unknown as OpenClawApp, { quiet: true });
  }

  if (evt.event === "exec.approval.requested") {
    const entry = parseExecApprovalRequested(evt.payload);
    if (entry) {
      host.execApprovalQueue = addExecApproval(host.execApprovalQueue, entry);
      host.execApprovalError = null;
      const delay = Math.max(0, entry.expiresAtMs - Date.now() + 500);
      window.setTimeout(() => {
        host.execApprovalQueue = removeExecApproval(host.execApprovalQueue, entry.id);
      }, delay);
    }
    return;
  }

  if (evt.event === "exec.approval.resolved") {
    const resolved = parseExecApprovalResolved(evt.payload);
    if (resolved) {
      host.execApprovalQueue = removeExecApproval(host.execApprovalQueue, resolved.id);
    }
    return;
  }

  if (evt.event === "fleet.question.requested") {
    const entry = parseFleetQuestionRequested(evt.payload);
    if (entry) {
      const app = host as unknown as OpenClawApp;
      // Clear the previous answer echo — a new question supersedes it.
      app.fleetQuestionLastAnswer = null;
      host.fleetQuestionQueue = addFleetQuestion(host.fleetQuestionQueue, entry);
      app.fleetQuestionError = null;
      app.fleetQuestionDraft = {};
      app.fleetQuestionFreeText = {};
      // Badge the session tab even when the operator is viewing another session/tab.
      if (entry.sessionKey.trim() !== host.sessionKey.trim()) {
        host.bumpSessionTabUnread?.(entry.sessionKey);
      }
      // If the gateway set a finite expiry, schedule the local
      // auto-removal; if expiresAtMs is the no-time-limit sentinel
      // (Number.MAX_SAFE_INTEGER), skip scheduling entirely so the
      // card stays until the operator answers or a NEW question
      // arrives (which `addFleetQuestion` replaces via id).
      const FARAWAY = 8_640_000_000_000_000; // ~ Year 2243
      if (entry.expiresAtMs < FARAWAY) {
        const delay = Math.max(0, entry.expiresAtMs - Date.now() + 500);
        window.setTimeout(() => {
          host.fleetQuestionQueue = removeFleetQuestion(host.fleetQuestionQueue, entry.id);
        }, delay);
      }
    }
    return;
  }

  if (evt.event === "fleet.question.resolved") {
    const resolved = parseFleetQuestionResolved(evt.payload);
    if (resolved) {
      host.fleetQuestionQueue = removeFleetQuestion(host.fleetQuestionQueue, resolved.id);
    }
    return;
  }

  if (evt.event === "fleet.todos.updated") {
    const updated = parseFleetTodosUpdated(evt.payload);
    if (updated) {
      const app = host as unknown as OpenClawApp;
      app.fleetTodosBySession = {
        ...app.fleetTodosBySession,
        [updated.sessionKey]: updated.todos,
      };
    }
    return;
  }

  if (evt.event === "session.artifact.recorded") {
    applySessionArtifactRecordedEvent(host as unknown as OpenClawApp, evt.payload);
  }
}

export async function refreshFleetSessionActivity(
  host: GatewayHost & {
    fleetSessionActivity?: FleetSessionActivityView | null;
    agentsSelectedId?: string | null;
  },
  sessionKey: string,
): Promise<void> {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return;
  }
  const activity = await loadFleetSessionActivity(host, { sessionKey: trimmed });
  if (activity && activity.sessionKey.trim() === host.sessionKey.trim()) {
    host.fleetSessionActivity = activity;
  }
}

export function applySnapshot(host: GatewayHost, hello: GatewayHelloOk) {
  const snapshot = hello.snapshot as
    | {
        presence?: PresenceEntry[];
        health?: HealthSnapshot;
        sessionDefaults?: SessionDefaultsSnapshot;
      }
    | undefined;
  if (snapshot?.presence && Array.isArray(snapshot.presence)) {
    host.presenceEntries = snapshot.presence;
  }
  if (snapshot?.health) {
    host.debugHealth = snapshot.health;
  }
  if (snapshot?.sessionDefaults) {
    applySessionDefaults(host, snapshot.sessionDefaults);
  }
}
